import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";

const STATUS_COLORS = {
  pending: "text-white/60 border-white/20",
  confirmed: "text-silver border-silver",
  packed: "text-silver border-silver",
  shipped: "text-denim-light border-denim-light",
  delivered: "text-white border-white",
  cancelled: "text-white/50 border-white/15 line-through",
};

export default function Orders() {
  const { isAuthenticated, openAuth, loading: authLoading } = useAuth();
  const { formatAmount } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    api.get("/orders").then((r) => setOrders(r.data.orders || [])).finally(() => setLoading(false));
  }, [isAuthenticated, authLoading]);

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main className="pt-28 md:pt-36 pb-20 px-6 md:px-12 lg:px-24" data-testid="orders-page">
        <div className="mb-12">
          <div className="eyebrow mb-3">Your Account</div>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-[0.95]">
            Order <span className="italic text-silver">history</span>.
          </h1>
        </div>

        {!isAuthenticated ? (
          <div className="border border-white/10 p-10 text-center">
            <p className="text-white/70 mb-4">Sign in to view your orders.</p>
            <button
              onClick={openAuth}
              data-testid="orders-signin"
              className="inline-flex border border-white/25 px-7 py-3 text-[11px] tracking-[0.32em] uppercase text-white hover:border-white"
            >Sign in</button>
          </div>
        ) : loading ? (
          <div className="text-white/50">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="border border-white/10 p-12 text-center">
            <div className="eyebrow mb-3 text-silver/70">Nothing yet</div>
            <h3 className="font-serif text-white text-3xl">No orders to display.</h3>
            <p className="text-white/55 mt-3">Once you make your first acquisition, it will live here.</p>
            <Link
              to="/"
              className="mt-8 inline-flex bg-white text-black px-7 py-3 text-[11px] tracking-[0.32em] uppercase hover:bg-silver"
            >Begin browsing</Link>
          </div>
        ) : (
          <ul className="space-y-6">
            {orders.map((o) => (
              <li key={o.order_id} className="border border-white/10 p-5 md:p-6" data-testid={`order-row-${o.order_id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
                  <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-silver/70">{o.order_id}</div>
                    <div className="text-white/50 text-xs mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-[10px] tracking-[0.3em] uppercase border ${STATUS_COLORS[o.status] || "text-white/60 border-white/20"}`}>
                      {o.status}
                    </span>
                    <span className="text-silver text-sm">{o.currency} {formatAmountFallback(o)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {o.items.map((it, idx) => (
                    <div key={idx} className="flex gap-3 items-center">
                      <div className="w-14 h-16 bg-midnight-panel overflow-hidden shrink-0">
                        <img src={it.image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-serif text-white text-sm leading-tight truncate">{it.name}</div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">
                          {it.size && <span className="mr-2">{it.size}</span>}
                          {it.color && <span className="mr-2">{it.color}</span>}
                          <span>×{it.quantity}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
      <FooterSection />
    </div>
  );
}

// Helper: orders endpoint already returns total numeric; show in stored currency
function formatAmountFallback(o) {
  const total = o.total;
  if (o.currency === "INR") return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(total);
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(total);
}
